import { observer } from "mobx-react";
import { SingleValue } from "react-select";
import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";
import FontBody2 from "../../../../../components/fonts/font-body2/font-body2";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../components/fonts/font-props";
import { FormFieldLine } from "../../../../../components/form-field-line/form-field-line";
import { IOption, SelectDropdown } from "../../../../../components/inputs/select-dropdown/select-dropdown";
import { Toggle } from "../../../../../components/inputs/toggle/toggle";
import Card from "../../../../../components/layouts/card/card";
import ControlGroup from "../../../../../components/layouts/control-group/control-group";
import { Note, NOTE_TYPE } from "../../../../../components/note/note";
import { FEATURES } from "../../../../../models/Plan";
import {
    OUT_OF_POLICY,
    OUT_OF_POLICY_MAP,
    RETURN_LABEL_FEE_PAYMENT,
    RETURN_LABEL_FEE_PAYMENT_MAP,
    RESTOCK_BY,
    RESTOCK_BY_MAP, LABEL_FEE_ROUNDING,
} from "../../../../../models/User";
import currentPortalStore from "../../../../../stores/current-portal-store";
import { FUNC } from "../../../../../stores/platform-service-store";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import DashTranslation from "../../../../../dashboard-translation/dashboard-translaton";
import Table from "../../../../../components/table/table";
import TableRow from "../../../../../components/table/components/table-row/table-row";
import shopifySettingsStore from "../../../../integrations/products-services/stores/shopify-settings-store";
import { useEffect } from "react";
import { Tooltip } from "../../../../../components/tooltip/tooltip";
import RMACancellationSettings from "./rma-cancellation-settings/rma-cancellation-settings";
import { IValidationGroup } from "../../../../../hooks/validation-group/validation-group-hook";
import {Input} from "../../../../../components/inputs/input/input";
import ResolveCapability from "../../../../../components/resolve-capability/resolve-capability";
import { PROFILE_CAPABILITIES } from "../../../../../models/ProfileCapabilities";

interface IProps {
    onExpandClick: (expanded: boolean) => void;
    isExpanded: boolean;
    validationGroup: IValidationGroup;
    dataName?: string; /** used by Candu to target an element */
}

const GeneralReturnsSettingsCard: React.FC<IProps> = observer(({ onExpandClick, isExpanded, validationGroup, dataName }) => {
    const { getTranslation } = useDashboardTranslation();
    const pageScope = 'dashboard-settings-store';

    const storeSetting = currentPortalStore.setting;
    const platform = currentPortalStore.platform;
    const notificationOnlyModeSettingStore = storeSetting?.notificationOnlyModeSettingStore;
    const platformName = platform?.getPlatformDisplayName() ?? 'platform';
    const returnLabelFeePaymentValue = storeSetting?.returnLabelFeePayment ? {
        label: getTranslation(RETURN_LABEL_FEE_PAYMENT_MAP[storeSetting.returnLabelFeePayment].label, pageScope),
        value: storeSetting.returnLabelFeePayment
    } : undefined;

    const onChangeReturnLabelFeePayment = (value: SingleValue<IOption>) => {
        storeSetting?.setReturnLabelFeePayment(value?.value as RETURN_LABEL_FEE_PAYMENT);
    }

    const outOfPolicyValue = storeSetting?.outOfPolicy ? {
        label: getTranslation(OUT_OF_POLICY_MAP[storeSetting.outOfPolicy].label, pageScope),
        value: storeSetting.outOfPolicy
    } : undefined;

    const restockByValue = storeSetting?.restockBy ? {
        label: getTranslation(RESTOCK_BY_MAP[storeSetting.restockBy].label, pageScope),
        value: storeSetting.restockBy
    } : undefined;

    const restockByNote = storeSetting?.restockBy === RESTOCK_BY.RESTOCK_BY_REFUND_PROCESS ?
        getTranslation(`WHEN_USED_RETURNGO_WILL_ASK_LINE_ITEM_REFUND_AND_MARK_RESTOCKED`, pageScope, [platformName]) :
        getTranslation(`WHEN_USED_RETURNGO_WILL_ASK_INCREASE_LINE_ITEM_INVENTORY`, pageScope, [platformName]);


    const onChangeOutOfPolicyValue = (value: SingleValue<IOption>) => {
        storeSetting?.setOutOfPolicy(value?.value as OUT_OF_POLICY);
    }

    const onChangeRestockByValue = (value: SingleValue<IOption>) => {
        storeSetting?.setRestockBy(value?.value as RESTOCK_BY);
    }

    const isGiftReturnsAllowed = storeSetting && currentPortalStore.isFeatureAllowed(FEATURES.GIFT_RETURNS);

    useEffect(() => {
        shopifySettingsStore.load()
    }, [])


    const getSelectedOptionForLabelFeeRoundingCondition = (): IOption | undefined => {
        return getOptionsForLabelFeeRoundingCondition().find(o => o.value === storeSetting?.getLabelFeeRoundingCondition());
    }

    const getOptionsForLabelFeeRoundingCondition = (): IOption[] => {
        return [
            { label: getTranslation('LABEL_FEE_ROUNDING_NO_ROUNDING', pageScope), value: LABEL_FEE_ROUNDING.LABEL_FEE_NO_ROUNDING },
            { label: getTranslation('LABEL_FEE_ROUNDING_NEAREST', pageScope), value: LABEL_FEE_ROUNDING.LABEL_FEE_ROUNDING_NEAREST },
            { label: getTranslation('LABEL_FEE_ROUNDING_UP', pageScope), value: LABEL_FEE_ROUNDING.LABEL_FEE_ROUNDING_UP },
            { label: getTranslation('LABEL_FEE_ROUNDING_DOWN', pageScope), value: LABEL_FEE_ROUNDING.LABEL_FEE_ROUNDING_DOWN }
        ];
    };

    return <Card
        toolbar={{
            title: (
                <FontBody1 variant={FONT_VARIANT.BOLD}>
                    <DashTranslation translationKey="GENERAL_RETURNS_SETTINGS" scope={pageScope} />
                </FontBody1>
            )
        }}
        expandCallback={onExpandClick}
        isExpanded={isExpanded}
        dataName={dataName}
    >
        { currentPortalStore.isFeatureAllowed(FEATURES.MANAGER_APPROVAL_SETTING_FEATURE) &&
            <FormFieldLine
                label={getTranslation('REQUIRE_MANAGER_APPROVAL', pageScope)}
                note={getTranslation('TO_APPROVE_RMA_ITS_REQUIRED_TO_GET_MANAGER_APPROVAL', pageScope)}
                dataName="require-manager-approval"
            >
                <Toggle
                    label=''
                    checked={storeSetting?.requireManagementApproval}
                    onChange={storeSetting?.setRequireManagementApproval}
                />
            </FormFieldLine>
        }
        <FormFieldLine
            label={getTranslation('ALLOW_MULTIPLE_RETURN_REQUESTS', pageScope)}
            note={getTranslation('WHEN_ENABLED_CUSTOMERS_WILL_BE_ABLE_TO_SUBMIT_MULTIPLE_RMA', pageScope)}
            dataName="allow-multiple-return-requests"
        >
            <Toggle
                label=''
                checked={storeSetting?.allowMultipleReturnRequests}
                onChange={storeSetting?.setAllowMultipleReturnRequests}
            />
        </FormFieldLine>
	    
	    <ResolveCapability capability={PROFILE_CAPABILITIES.WRITE_RMA_EVENTS_IN_ORDER_NOTES}>
		    {({ isSupported }) => isSupported && (
			    <FormFieldLine
				    tooltip={
					    <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
						    <DashTranslation translationKey="NOTE_SOME_STORE_THEMES_INCLUDE_ORDER_NOTES" scope={pageScope} />
					    </FontBody2>
				    }
				    label={getTranslation('WRITE_RMA_EVENTS_IN_ORDER_NOTES', pageScope)}
				    note={getTranslation('WHEN_ENABLED_RMA_STATUS_CHANGES_WILL_GO_TO_ORDER_NOTES', pageScope)}
				    dataName="write-rma-events-in-order-notes"
			    >
				    <Toggle
					    checked={storeSetting?.enableOrderNotes}
					    onChange={storeSetting?.setEnableOrderNotes}
					    label=''
				    />
			    </FormFieldLine>
		    )}
	    </ResolveCapability>

	    <ResolveCapability capability={PROFILE_CAPABILITIES.FEE_TAXES_CALCULATION}>
		    {({ isSupported }) => (isSupported && platform?.isFunctionalitySupported(FUNC.FUNC_CHARGE_TAXES_FOR_FEES)) && (
				<FormFieldLine
	                label={getTranslation('FEE_TAX_CALCULATION', pageScope)}
	                note={getTranslation('SET_WHETHER_TO_ADD_TAX_TO_FEES', pageScope)}
	                dataName="fee-tax-calculation"
	            >
	                <Table
	                    columns={[
	                        {
	                            content: getTranslation('FEE_TYPE', pageScope),
	                            width: {
	                                value: 1,
	                                type: 'fr'
	                            }
	                        },
	                        {
	                            content: getTranslation('ADD_TAX', pageScope),
	                            width: {
	                                value: 1,
	                                type: 'fr'
	                            }
	                        },
	                    ]}
	                    totalResultsTitle='Fee Tax Calculation Setting'
	                >
	                    {storeSetting?.returngoFeesSetting.map(returngoFeesSetting => (
	                        <TableRow
	                            key={returngoFeesSetting.feeType}
	                            cells={[
	                                getTranslation(returngoFeesSetting.feeType, pageScope),
	                                <Toggle
	                                    checked={returngoFeesSetting.addTax}
	                                    onChange={(v) => returngoFeesSetting.setAddTax(v)}
	                                    label=''
	                                />
	                            ]}
	                        />
	                    ))}
	                </Table>
	            </FormFieldLine>
		    )}
	    </ResolveCapability>
        {platform?.isFunctionalitySupported(FUNC.FUNC_SETTING_LABEL_FEE_PAYMENT) && (
            <>
                <FormFieldLine
                    label={getTranslation('RETURN_LABEL_FEE_PAYMENT', pageScope)}
                    note={getTranslation('DEFINE_HOW_TO_CHARGE_RETURN_LABEL_FEE', pageScope)}
                    dataName="return-label-fee-payment"
                >
                    <SelectDropdown
                        options={[
                            {
                                label: getTranslation(RETURN_LABEL_FEE_PAYMENT_MAP[RETURN_LABEL_FEE_PAYMENT.ALWAYS_CHARGE].label, pageScope),
                                value: RETURN_LABEL_FEE_PAYMENT.ALWAYS_CHARGE
                            },
                            {
                                label: getTranslation(RETURN_LABEL_FEE_PAYMENT_MAP[RETURN_LABEL_FEE_PAYMENT.DEDUCT_FROM_REFUNDS].label, pageScope),
                                value: RETURN_LABEL_FEE_PAYMENT.DEDUCT_FROM_REFUNDS
                            },
                            {
                                label: getTranslation(RETURN_LABEL_FEE_PAYMENT_MAP[RETURN_LABEL_FEE_PAYMENT.SET_PER_POLICY_RULE].label, pageScope),
                                value: RETURN_LABEL_FEE_PAYMENT.SET_PER_POLICY_RULE
                            }
                        ]}
                        onChange={onChangeReturnLabelFeePayment}
                        value={returnLabelFeePaymentValue}
                    />
                </FormFieldLine>
                <ControlGroup>
                    <Note
                        type={NOTE_TYPE.INFORMATION}
                        body={<DashTranslation translationKey="IF_CUSTOMER_HAS_MULTIPLE_ITEMS_WITH_DIFFERENT_FEES_WILL_HAVE_SEPARATE_CHARGES" scope={pageScope} />}
                    />
                </ControlGroup>
            </>

        )}
        { currentPortalStore.isFeatureAllowed(FEATURES.BETA_ROUNDED_LABEL_FEE_COST) &&
            <>
                { storeSetting &&
                    <FormFieldLine
                        label={getTranslation('LABEL_FEE_ROUNDING_LABEL', pageScope)}
                        note={getTranslation('LABEL_FEE_ROUNDING_NOTE', pageScope)}
                        dataName="label-fee-rounding"
                    >
                        <SelectDropdown
                            options={getOptionsForLabelFeeRoundingCondition()}
                            value={getSelectedOptionForLabelFeeRoundingCondition()}
                            onChange={storeSetting.setLabelFeeRoundingCondition}
                        />
                    </FormFieldLine>
                }
                { storeSetting && storeSetting.getLabelFeeRoundingCondition() !== LABEL_FEE_ROUNDING.LABEL_FEE_NO_ROUNDING &&
                    <FormFieldLine
                        label={''}
                        note={''}
                        dataName="label-fee-rounding-amount"
                    >
                        <div style={{display: 'flex', gap: '24px'}}>
                            <FontBody2>{getTranslation('LABEL_FEE_ROUNDING_AMOUNT', pageScope)}</FontBody2>
                            <div style={{flex: 1}}>
                                <Input type="number"
                                       temp
                                       value={storeSetting.getLabelFeeRoundingAmount()}
                                       onChange={storeSetting.setLabelFeeRoundingAmount}
                                       min={0.01}
                                       max={10000}
                                       step={0.01}
                                       validationGroup={validationGroup}
                                       required={true}
                                />
                            </div>
                        </div>
                    </FormFieldLine>
                }
            </>
        }
        <FormFieldLine
            label={getTranslation('HANDLING_OUT_OF_POLICY_ITEMS_IN_PORTAL', pageScope)}
            note={getTranslation('DEFINE_HOW_TO_HANDLE_ITEMS_INELIGIBLE_ON_FINAL_STEP', pageScope)}
            dataName="handling-out-of-policy-items"
        >
            <SelectDropdown
                options={[
                    {
                        label: getTranslation(OUT_OF_POLICY_MAP[OUT_OF_POLICY.ITEM_INCLUDED].label, pageScope),
                        value: OUT_OF_POLICY.ITEM_INCLUDED
                    },
                    {
                        label: getTranslation(OUT_OF_POLICY_MAP[OUT_OF_POLICY.ITEM_REFERENCED].label, pageScope),
                        value: OUT_OF_POLICY.ITEM_REFERENCED
                    },
                    {
                        label: getTranslation(OUT_OF_POLICY_MAP[OUT_OF_POLICY.ITEM_NOT_CREATED].label, pageScope),
                        value: OUT_OF_POLICY.ITEM_NOT_CREATED
                    }
                ]}
                onChange={onChangeOutOfPolicyValue}
                value={outOfPolicyValue}
            />
        </FormFieldLine>
        <ControlGroup>
            <Note
                type={NOTE_TYPE.INFORMATION}
                body={<DashTranslation translationKey="WHEN_SOME_ITEMS_ARE_OUT_OF_POLICY_PORTAL_WILL_NOT_ALLOW_TO_CREATE_RMA" scope={pageScope} />}
            />
        </ControlGroup>

        <FormFieldLine
            label={getTranslation('RESTOCK_BY', pageScope)}
            note={getTranslation('DEFINE_HOW_TO_EXECUTE_LINE_ITEM_RESTOCKING', pageScope)}
            dataName="restock-by"
        >
            <Tooltip
              content={
                  shopifySettingsStore.isUseShopifyReturnsApi && (
                    <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                        <DashTranslation translationKey="RESTOCK_BY_SET_TO_INCREASING_INVENTORY_WITH_SHOPIFY_RETURNS_NOTE" scope={pageScope}/>
                    </FontBody2>
                  )
              }
            >
                <SelectDropdown
                    options={[
                        {
                            label: getTranslation(RESTOCK_BY_MAP[RESTOCK_BY.RESTOCK_BY_REFUND_PROCESS].label, pageScope),
                            value: RESTOCK_BY.RESTOCK_BY_REFUND_PROCESS,
                            disabled: notificationOnlyModeSettingStore?.isActive && notificationOnlyModeSettingStore?.refundToPaymentMethod
                        },
                        {
                            label: getTranslation(RESTOCK_BY_MAP[RESTOCK_BY.RESTOCK_BY_INCREASING_INVENTORY].label, pageScope),
                            value: RESTOCK_BY.RESTOCK_BY_INCREASING_INVENTORY
                        }
                    ]}
                    onChange={onChangeRestockByValue}
                    value={restockByValue}
                    disabled={shopifySettingsStore.isUseShopifyReturnsApi}
                />
            </Tooltip>
        </FormFieldLine>
        <ControlGroup>
            <Note
                type={NOTE_TYPE.INFORMATION}
                body={restockByNote}
            />
        </ControlGroup>
        {platform?.isFunctionalitySupported(FUNC.FUNC_SETTING_GIFT_RETURNS) && (
            <>
                <FormFieldLine
                    label={getTranslation('ALLOW_GIFT_REFUNDS', pageScope)}
                    note={getTranslation('ADD_A_RETURNING_GIFT_LINK_AND_FLOW', pageScope)}
                    tooltip={!isGiftReturnsAllowed ? (
                        <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                            <DashTranslation translationKey="FEATURE_NOT_INCLUDED_IN_YOUR_PLAN" scope="dashboard-general" />
                        </FontBody2>
                    ) : ''}
                    dataName="allow-gift-refunds"
                >
                    <Toggle
                        checked={storeSetting?.enableGiftReturns}
                        onChange={storeSetting?.setEnableGiftReturns}
                        label=''
                        disabled={!isGiftReturnsAllowed}

                    />
                </FormFieldLine>
                {isGiftReturnsAllowed && (
                    <ControlGroup>
                        <Note
                            type={NOTE_TYPE.INFORMATION}
                            body={<DashTranslation translationKey="GIFTS_MUST_BE_DEFINED_AS_ELIGIBLE" scope={pageScope} />}
                        />
                    </ControlGroup>
                )}
            </>
        )}
        {currentPortalStore.isFeatureAllowed(FEATURES.CANCEL_INACTIVE_RMAS_FEATURE) &&
            <>
                <FormFieldLine
                    label={getTranslation('CANCEL_INACTIVE_RMA_TOGGLE', pageScope)}
                    note={getTranslation('CANCEL_INACTIVE_RMA_TOGGLE_DESCRIPTION', pageScope)}
                    dataName="cancel-inactive-rma-toggle"
                >
                    <Toggle
                        label=''
                        checked={storeSetting?.cancelInactiveRmas}
                        onChange={v => storeSetting?.setCancelInactiveRmas(v)}
                    />
                </FormFieldLine>
                { storeSetting?.cancelInactiveRmas && <RMACancellationSettings validationGroup={validationGroup} storeSetting={storeSetting} /> }
            </>
        }
        {currentPortalStore.isFeatureAllowed(FEATURES.BETA_CANCEL_SHIPMENT_LABEL) &&
            <FormFieldLine
                label={getTranslation('CANCEL_SHIPMENT_LABEL_ON_RMA_CANCEL_REJECT_TITLE', pageScope)}
                note={getTranslation('CANCEL_SHIPMENT_LABEL_ON_RMA_CANCEL_REJECT_DESCRIPTION', pageScope)}
                dataName="cancel-shipment-label-on-rma-cancel-reject"
            >
                <Toggle
                    checked={storeSetting?.isCancelShipmentLabelEnabled}
                    onChange={storeSetting?.setIsCancelShipmentLabelEnabled}
                    label=''
                />
            </FormFieldLine>
        }

        <FormFieldLine
            label={getTranslation('DISCREET_SENDER', pageScope)}
            note={getTranslation('DISCREET_SENDER_DESCRIPTION', pageScope)}
            dataName="discreet-sender"
        >
            <Toggle
                checked={storeSetting?.hasDiscreetSenderEnabled}
                onChange={storeSetting?.setDiscreetSenderEnabled}
                label=''
            />
        </FormFieldLine>
    </Card>
});

export default GeneralReturnsSettingsCard;
